import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@apfasttrack.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Authenticated. Entering control room.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden border-r border-[#A1AEC2]/15">
        <div className="absolute inset-0 aegis-grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#060C1F] via-[#0A1024] to-[#12182B]" />
        <div className="relative m-auto p-16 max-w-md">
          <ShieldCheck className="w-20 h-20 text-[#D4AF37]" strokeWidth={1.2} />
          <h2 className="mt-10 font-display text-4xl font-bold text-[#F0F4F8] leading-tight">
            Operations Control Room
          </h2>
          <p className="mt-4 text-[#9EABC0]">
            Cleared personnel only. All activity is logged, watermarked and cryptographically signed.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-4">
            <div className="border border-[#D4AF37]/40 p-4">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">Clearance</div>
              <div className="mt-2 font-display text-[#F0F4F8]">Tier IV</div>
            </div>
            <div className="border border-[#A1AEC2]/25 p-4">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9EABC0]">Uptime</div>
              <div className="mt-2 font-display text-[#F0F4F8]">99.997%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Secure Access</div>
              <h1 className="font-display text-3xl font-bold text-[#F0F4F8]">Admin Portal</h1>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-2 w-full bg-[#060C1F] border border-[#A1AEC2]/30 text-[#F0F4F8] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] px-4 py-3 outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">Passcode</label>
              <input
                required
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-2 w-full bg-[#060C1F] border border-[#A1AEC2]/30 text-[#F0F4F8] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] px-4 py-3 outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-8 w-full bg-[#D4AF37] text-[#060C1F] font-bold uppercase tracking-widest py-4 hover:bg-[#C5A059] disabled:opacity-60 flex items-center justify-center gap-3"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Authenticate
          </button>

          <div className="mt-8 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#9EABC0]">
            Session encrypted · TLS 1.3
          </div>
        </form>
      </div>
    </div>
  );
}
